/**
 * The price build-up for a merchandise draft estimate, in place of the
 * Tharstern / pricing-policy / carriage columns an ordinary estimate shows.
 *
 * Awaiting quote: an honest empty state — the request, the ticket to the
 * merchandise team and how long it has been open — never a £0.00.
 * Quoted (or held on Quote changed): Buy price → margin → Quoted price, the
 * merchandise team's Promo Office price taken as is (Ben's ruling 1), with
 * One's own policy price beside it for comparison and the guardrails' flag
 * when the price is thin.
 *
 * Where Send used to be there is now a stated note. Slice A has no send at
 * all, and a greyed button with nothing to explain it reads as a fault the CSR
 * has caused; the sentence says what is actually true.
 */
import { Clock } from "lucide-react";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import type { MoneyRow } from "@shared-core/domain/estimate-presentation";
import {
  formatDateTimeOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  DRAFT_ESTIMATE_STATE_LABELS,
  describeRequestLineCount,
  describeOpenFor,
  describeQuotedAgainstPolicy,
  draftEstimatePriceFlag,
  draftEstimatePriceLadder,
  draftSendBlockReason,
} from "@shared-core/domain/merchandise-presentation";

const ROW_STYLES: Record<MoneyRow["kind"], string> = {
  line: "text-sm",
  subtotal: "text-sm font-medium border-t border-border pt-1.5 mt-1.5",
  uplift: "text-sm",
  total: "text-sm font-semibold border-t border-border pt-1.5 mt-1.5",
  aside:
    "text-sm text-muted-foreground border-t border-dashed border-border pt-1.5 mt-1.5",
};

export function DraftEstimatePriceBuildUp({
  request,
}: {
  request: MerchandiseDraftEstimate;
}) {
  const pricing = request.pricing;

  if (!pricing) {
    return (
      <section aria-label="Price build-up" className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-muted-foreground" aria-hidden />
          {/* The draft's own state word, shared with the badge and the detail
              page, so a withdrawn request that never got a price does not read
              as still waiting for one. */}
          <h4 className="text-sm font-semibold">
            {DRAFT_ESTIMATE_STATE_LABELS[request.state]}
          </h4>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">
          The price is the merchandise team&apos;s Promo Office price, taken
          once they have quoted — nothing is shown until One holds a real quote.
        </p>
        <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 max-w-2xl">
          <div>
            <dt className="text-xs text-muted-foreground">Request</dt>
            <dd>
              {request.request.projectName}
              <span className="block text-xs text-muted-foreground">
                {describeRequestLineCount(request.request.lines.length)}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              Ticket to the merchandise team
            </dt>
            {/* Null when ticket creation failed — a real state, not a bug: the
                request still stands, so the panel says so rather than showing
                an empty line. */}
            <dd>
              {request.merchandiseTeamTicket ? (
                <>
                  <span className="font-mono">
                    {request.merchandiseTeamTicket.id}
                  </span>
                  {request.merchandiseTeamTicket.raisedAt && (
                    <>
                      {" · "}
                      open for{" "}
                      {describeOpenFor(request.merchandiseTeamTicket.raisedAt)}
                    </>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">
                  No ticket was raised — tell the merchandise team directly,
                  quoting {request.reference}.
                </span>
              )}
            </dd>
          </div>
        </dl>
        <p className="max-w-prose border-t border-border pt-3 text-xs text-muted-foreground">
          {draftSendBlockReason(request)}
        </p>
      </section>
    );
  }

  const rows = draftEstimatePriceLadder(pricing);
  const quote = request.linkedQuote;
  const flag = draftEstimatePriceFlag(pricing);
  const comparison = describeQuotedAgainstPolicy(pricing);

  return (
    <section aria-label="Price build-up" className="space-y-4">
      <div>
        <div className="text-xs text-muted-foreground mb-1">Quoted price</div>
        <div className="text-2xl font-semibold tabular-nums">
          {formatQuotedPrice(pricing.ourPricePence)}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          The merchandise team&apos;s price on Promo Office quote{" "}
          {quote ? quote.promoOfficeQuoteNumber : "not recorded"}, priced{" "}
          {formatDateTimeOrDash(pricing.pricedAt)}
        </div>
        {/* The guardrails' own verdict on the team's price, in their own
            words. A flag, never a refusal: the frontend holds no threshold of
            its own and renders the sentence the backend stored. */}
        {flag && (
          <p
            className={`mt-2 max-w-prose text-xs font-medium ${
              flag.tone === "danger"
                ? "text-red-700 dark:text-red-400"
                : "text-amber-700 dark:text-amber-400"
            }`}
          >
            {flag.text}
          </p>
        )}
      </div>

      <dl className="space-y-1 max-w-md">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`flex justify-between gap-4 ${ROW_STYLES[row.kind]}`}
          >
            <dt className={row.kind === "line" ? "text-muted-foreground" : ""}>
              {row.label}
              {row.pct != null && (
                <span className="text-muted-foreground"> ({row.pct}%)</span>
              )}
            </dt>
            <dd className="tabular-nums whitespace-nowrap">
              {formatQuotedPrice(row.pence)}
            </dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground max-w-prose">
        The quoted price is the merchandise team&apos;s Promo Office price and
        is what the customer will see.
        {comparison && <> It is {comparison}.</>} One's policy price is what
        this customer&apos;s price group would charge for the same buy price,
        shown to compare against; it never changes the quoted price.
      </p>

      <p className="max-w-prose border-t border-border pt-3 text-xs text-muted-foreground">
        {draftSendBlockReason(request)}
      </p>
    </section>
  );
}
